import { createSlice } from '@reduxjs/toolkit'

import { ApiError, NameSpace, SliceStatus } from 'src/Redux/types'
import {
  CampaignPlatformTypeEnum,
  CampaignTypeEnum,
  TiktokCampaign,
  TiktokCampaignStatus,
} from 'src/Types/campaigns'
import { setApiError } from 'src/Hooks/redux'
import { getDefaultStartDate } from 'src/Constants/functions'

import {
  getCampaignTikTokCampaignToScheduleDetails,
  updateTikTokCampaign,
} from './api-actions'

export interface TiktokCampaignState {
  campaign: TiktokCampaign
  error: ApiError | null
  status: SliceStatus
}

const initialState: TiktokCampaignState = {
  campaign: {
    id: '',
    type: CampaignTypeEnum.BASIC,
    platformType: CampaignPlatformTypeEnum.TIKTOK,
    trackDuration: 0,
    totalVideos: 0,
    trackTitle: '',
    pricingModel: null,
    commentType: '',
    lyricsLink: null,
    spent: 0,
    reserved: 0,
    totalShares: 0,
    totalViews: 0,
    trackAuthorName: '',
    trackId: 0,
    status: TiktokCampaignStatus.PENDING,
    startDate: getDefaultStartDate(),
    submittedVideosAmount: 0,
    influencersAcceptedAmount: 0,
    endDate: '',
    isCurrent: false,
    totalRefunded: 0,
    relaunched: false,
    reviews: {
      done: 0,
      influencersAcceptedCount: 0,
    },
  },
  error: null,
  status: SliceStatus.Idle,
}

export const tiktokCampaignSlice = createSlice({
  initialState,
  name: NameSpace.TiktokCampaign,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(updateTikTokCampaign.pending, (state) => {
      state.status = SliceStatus.Loading
    })
    builder.addCase(updateTikTokCampaign.fulfilled, (state, { payload }) => {
      const prevCampaign = state.campaign

      state.campaign = {
        ...prevCampaign,
        ...payload,
        type: payload.type ?? prevCampaign?.type,
        trackDuration:
          payload.track?.duration ?? prevCampaign?.trackDuration ?? 0,
        pricingModel: payload.pricingModel ?? prevCampaign?.pricingModel,
        startDate: payload.startDate ?? prevCampaign?.startDate,
      }
      state.status = SliceStatus.Succeeded
    })
    builder.addCase(updateTikTokCampaign.rejected, (state, action) => {
      state.error = setApiError(action)
      state.status = SliceStatus.Failed
    })

    // getCampaignTikTokCampaignToScheduleDetails
    builder.addCase(
      getCampaignTikTokCampaignToScheduleDetails.pending,
      (state) => {
        state.status = SliceStatus.Loading
      },
    )
    builder.addCase(
      getCampaignTikTokCampaignToScheduleDetails.fulfilled,
      (state, { payload }) => {
        const prevType = state.campaign?.type
        const prevTrackDuration = state.campaign?.trackDuration
        state.campaign = {
          ...payload,
          type: payload.type ?? prevType,
          trackDuration: payload.track?.duration ?? prevTrackDuration ?? 0,
        }
        state.status = SliceStatus.Succeeded
      },
    )
    builder.addCase(
      getCampaignTikTokCampaignToScheduleDetails.rejected,
      (state, action) => {
        state.error = setApiError(action)
        state.status = SliceStatus.Failed
      },
    )
  },
})
